import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, User, Car, Receipt, Wallet, Loader2, ClipboardList, Calendar, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_ICON = {
  customer: User,
  vehicle: Car,
  invoice: Receipt,
  job_card: ClipboardList,
  booking: Calendar,
  // iter139.7 — Quote results from /api/global-search
  quote: FileText,
  account: Wallet,
};
const TYPE_LABEL = {
  customer: 'Customers',
  vehicle: 'Vehicles',
  invoice: 'Invoices',
  job_card: 'Job Cards',
  booking: 'Bookings',
  quote: 'Quotes',
  account: 'Accounts',
};

export default function GlobalSearch() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Cmd/Ctrl + K shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const runSearch = useCallback(async (term) => {
    if (!term || term.length < 1) { setResults(null); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/global-search?q=${encodeURIComponent(term)}&limit_each=5`, {
        credentials: 'include',
      });
      if (res.ok) setResults(await res.json());
    } catch (_e) { /* noop */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runSearch(q), 250);
    return () => clearTimeout(t);
  }, [q, runSearch]);

  const groups = ['customers', 'vehicles', 'job_cards', 'bookings', 'quotes', 'invoices', 'accounts']
    .map((k) => ({ key: k, items: results?.[k] || [] }))
    .filter((g) => g.items.length > 0);
  const totalHits = groups.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <div ref={wrapRef} className="relative w-full max-w-xl" data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder="Search rego, customer, mobile, invoice or job card #…  (⌘K)"
          className="w-full pl-9 pr-9 py-2 bg-slate-100 hover:bg-white focus:bg-white border border-transparent focus:border-slate-300 rounded-full text-sm transition-colors focus:outline-none"
          data-testid="global-search-input"
        />
        {q && (
          <button
            onClick={() => { setQ(''); setResults(null); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-slate-200"
          >
            <X className="h-3.5 w-3.5 text-slate-500" />
          </button>
        )}
      </div>

      {open && q && (
        <div
          className="absolute z-50 mt-2 w-full bg-white border border-slate-200 rounded-xl shadow-2xl max-h-[28rem] overflow-y-auto"
          data-testid="global-search-results"
        >
          {loading && (
            <div className="p-4 text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Searching…
            </div>
          )}
          {!loading && totalHits === 0 && (
            <div className="p-6 text-sm text-slate-500 text-center">
              No matches for "<span className="font-medium">{q}</span>". Try a rego, mobile, name or invoice number.
            </div>
          )}
          {!loading && groups.map((g) => (
            <div key={g.key} className="border-b border-slate-100 last:border-0">
              <div className="px-3 py-1.5 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                {TYPE_LABEL[g.key] || TYPE_LABEL[g.key.replace(/s$/, '')] || g.key}
              </div>
              {g.items.map((r) => {
                const Icon = TYPE_ICON[r.type] || Search;
                return (
                  <button
                    key={`${r.type}-${r.id}`}
                    onClick={() => { setOpen(false); setQ(''); navigate(r.href); }}
                    className="w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-start gap-3 border-b border-slate-50 last:border-0"
                    data-testid={`gs-${r.type}-result`}
                  >
                    <Icon className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{r.title}</p>
                      {r.subtitle && <p className="text-xs text-slate-500 truncate">{r.subtitle}</p>}
                    </div>
                    {r.tag && (
                      <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded shrink-0">
                        {r.tag}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
